import React, { useState } from 'react';
import DevConsole from './DevConsole';
import LLMPhasesView from './LLMPhasesView';
import { buildImageUrl } from '../../services/loucaApi';

/**
 * Vue développeur avec 10+ onglets pour afficher toutes les données
 */
const DevViewTabs = ({ result, sessionData, logDetailLevel = 'standard' }) => {
  const [activeTab, setActiveTab] = useState('console');

  const tabs = [
    { id: 'console', label: 'Console', icon: '📝' },
    { id: 'phase1', label: 'Phase 1', icon: '🔍' },
    { id: 'phase2', label: 'Phase 2', icon: '📝' },
    { id: 'extracted', label: 'Images Extraites', icon: '🖼️' },
    { id: 'grids', label: 'Images Grille', icon: '📐' },
    { id: 'bboxes', label: 'Bounding Boxes', icon: '📦' },
    { id: 'stats', label: 'Statistiques', icon: '📊' },
    { id: 'session', label: 'Session', icon: '💾' },
    { id: 'config', label: 'Config', icon: '⚙️' },
    { id: 'rejected', label: 'Rejetées', icon: '❌' },
    { id: 'raw', label: 'Raw JSON', icon: '🔧' },
    { id: 'timing', label: 'Timing', icon: '⏱️' },
  ];

  const consoleLogs = result?.console_logs || [];
  const pipelineStats = result?.pipeline_stats || {};
  const devView = result?.dev_view || {};
  const stepTimings = result?.step_timings || {};

  // Images extraites
  const extractedImages = devView.extracted_images || [];
  
  // Images avec grille
  const gridImages = devView.grid_images || [];
  
  // Bounding boxes
  const bboxImages = devView.bbox_images || [];
  
  // Images rejetées
  const rejectedBlur = devView.rejected_blur_urls || [];
  const rejectedNotCar = devView.rejected_not_car_urls || [];
  const rejectedDuplicates = devView.rejected_duplicates_urls || [];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'console':
        return <DevConsole consoleLogs={consoleLogs} logDetailLevel={logDetailLevel} />;
      
      case 'phase1':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <LLMPhasesView result={result} selectedPhase="phase1" />
          </div>
        );
      
      case 'phase2':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <LLMPhasesView result={result} selectedPhase="phase2" />
          </div>
        );
      
      case 'extracted':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Images Extraites</h2>
            <p className="text-sm text-slate-400 mb-4">
              {extractedImages.length} image(s) extraite(s)
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {extractedImages.map((img, idx) => (
                <a
                  key={idx}
                  href={buildImageUrl(img.url || img.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-slate-900 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <img
                    src={buildImageUrl(img.url || img.path)}
                    alt={img.filename || `Image ${idx}`}
                    className="w-full h-40 object-cover group-hover:opacity-90 transition"
                  />
                  <div className="p-2 text-xs text-slate-300 truncate">
                    {img.filename || `Image ${idx}`}
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      
      case 'grids':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Images avec Grille</h2>
            <p className="text-sm text-slate-400 mb-4">
              {gridImages.length} image(s) avec grille
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gridImages.map((img, idx) => (
                <a
                  key={idx}
                  href={buildImageUrl(img.url || img.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-slate-900 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <img
                    src={buildImageUrl(img.url || img.path)}
                    alt={img.filename || `Grid ${idx}`}
                    className="w-full h-40 object-cover group-hover:opacity-90 transition"
                  />
                  <div className="p-2 text-xs text-slate-300 truncate">
                    {img.filename || `Grid ${idx}`}
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      
      case 'bboxes':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Bounding Boxes</h2>
            <p className="text-sm text-slate-400 mb-4">
              {bboxImages.length} image(s) avec bounding boxes
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {bboxImages.map((img, idx) => (
                <a
                  key={idx}
                  href={buildImageUrl(img.url || img.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-slate-900 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <img
                    src={buildImageUrl(img.url || img.path)}
                    alt={img.filename || `BBox ${idx}`}
                    className="w-full h-40 object-cover group-hover:opacity-90 transition"
                  />
                  <div className="p-2 text-xs text-slate-300 truncate">
                    {img.filename || `BBox ${idx}`}
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      
      case 'stats':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Statistiques Pipeline</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Total Frames</p>
                <p className="text-2xl font-bold text-white">{pipelineStats.total_frames || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Rejetées (Flou)</p>
                <p className="text-2xl font-bold text-red-400">{pipelineStats.rejected_blur || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Rejetées (Pas Voiture)</p>
                <p className="text-2xl font-bold text-red-400">{pipelineStats.rejected_not_car || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Rejetées (Doublons)</p>
                <p className="text-2xl font-bold text-red-400">{pipelineStats.rejected_duplicate || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Après Filtrage</p>
                <p className="text-2xl font-bold text-green-400">{pipelineStats.after_blur_filter || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Après Détection Voiture</p>
                <p className="text-2xl font-bold text-green-400">{pipelineStats.after_car_filter || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Après Déduplication</p>
                <p className="text-2xl font-bold text-green-400">{pipelineStats.after_dedup || 0}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-400">Sélectionnées (KMeans)</p>
                <p className="text-2xl font-bold text-orange-400">{pipelineStats.selected_kmeans || 0}</p>
              </div>
            </div>
          </div>
        );
      
      case 'session':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Informations Session</h2>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <pre className="text-xs text-slate-300 overflow-x-auto">
                {JSON.stringify(sessionData, null, 2)}
              </pre>
            </div>
          </div>
        );
      
      case 'config':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Configuration</h2>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <pre className="text-xs text-slate-300 overflow-x-auto">
                {JSON.stringify(sessionData?.config || {}, null, 2)}
              </pre>
            </div>
          </div>
        );
      
      case 'rejected':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Images Rejetées</h2>
            </div>
            
            {rejectedBlur.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-white mb-2">Rejetées (Flou) - {rejectedBlur.length}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {rejectedBlur.map((url, idx) => (
                    <a
                      key={idx}
                      href={buildImageUrl(url)}
                      target="_blank"
                      rel="noreferrer"
                      className="group block bg-slate-900 border border-red-500/30 rounded-xl overflow-hidden"
                    >
                      <img
                        src={buildImageUrl(url)}
                        alt={`Rejected blur ${idx}`}
                        className="w-full h-32 object-cover group-hover:opacity-90 transition"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {rejectedNotCar.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-white mb-2">Rejetées (Pas Voiture) - {rejectedNotCar.length}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {rejectedNotCar.map((url, idx) => (
                    <a
                      key={idx}
                      href={buildImageUrl(url)}
                      target="_blank"
                      rel="noreferrer"
                      className="group block bg-slate-900 border border-red-500/30 rounded-xl overflow-hidden"
                    >
                      <img
                        src={buildImageUrl(url)}
                        alt={`Rejected not car ${idx}`}
                        className="w-full h-32 object-cover group-hover:opacity-90 transition"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {rejectedDuplicates.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-white mb-2">Rejetées (Doublons) - {rejectedDuplicates.length}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {rejectedDuplicates.map((url, idx) => (
                    <a
                      key={idx}
                      href={buildImageUrl(url)}
                      target="_blank"
                      rel="noreferrer"
                      className="group block bg-slate-900 border border-red-500/30 rounded-xl overflow-hidden"
                    >
                      <img
                        src={buildImageUrl(url)}
                        alt={`Rejected duplicate ${idx}`}
                        className="w-full h-32 object-cover group-hover:opacity-90 transition"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
            
            {rejectedBlur.length === 0 && rejectedNotCar.length === 0 && rejectedDuplicates.length === 0 && (
              <p className="text-slate-400 text-center py-8">Aucune image rejetée</p>
            )}
          </div>
        );
      
      case 'raw':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Raw JSON</h2>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700 max-h-[600px] overflow-auto">
              <pre className="text-xs text-slate-300">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          </div>
        );
      
      case 'timing':
        return (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Timing des Étapes</h2>
            <div className="space-y-4">
              {Object.entries(stepTimings).map(([step, timing]) => (
                <div key={step} className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{step}</span>
                    <span className="text-orange-400 font-mono">{timing}s</span>
                  </div>
                </div>
              ))}
              {result?.processing_time && (
                <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-500/30">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-bold">Temps Total</span>
                    <span className="text-orange-400 font-mono text-xl">{result.processing_time}s</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-2">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>{renderTabContent()}</div>
    </div>
  );
};

export default DevViewTabs;

